import { supabase } from '../lib/supabase';

// Case conversion helpers
const toSnakeCase = (obj) => {
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
};

const toCamelCase = (obj) => {
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
};

export const invoiceService = {
  /**
   * Get all invoices (with tenant filtering)
   */
  async getAll(tenantId = null, isSuperAdmin = false) {
    try {
      let query = supabase?.from('invoices')?.select(`
          *,
          customer:customers(id, company_name, contact_person, email),
          created_by_user:user_profiles!created_by(first_name, last_name, email),
          invoice_items(*)
        `)?.order('created_at', { ascending: false });

      // Apply tenant filter ONLY for non-Super Admins
      // Super Admins see ALL invoices across ALL tenants
      if (!isSuperAdmin && tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(invoice => ({
        id: invoice?.id,
        tenantId: invoice?.tenant_id,
        customerId: invoice?.contact_id,
        invoiceNumber: invoice?.invoice_number,
        invoiceDate: invoice?.invoice_date,
        dueDate: invoice?.due_date,
        status: invoice?.status,
        paymentStatus: invoice?.payment_status,
        paymentTerms: invoice?.payment_terms,
        customTerms: invoice?.custom_terms,
        subtotal: invoice?.subtotal,
        taxRate: invoice?.tax_rate,
        taxAmount: invoice?.tax_amount,
        discountType: invoice?.discount_type,
        discountValue: invoice?.discount_value,
        discountAmount: invoice?.discount_amount,
        shippingAmount: invoice?.shipping_amount,
        totalAmount: invoice?.total_amount,
        notes: invoice?.notes,
        customer: invoice?.customer,
        createdBy: invoice?.created_by_user,
        items: invoice?.invoice_items?.map(item => ({
          id: item?.id,
          description: item?.description,
          quantity: item?.quantity,
          unitPrice: item?.unit_price,
          totalPrice: item?.total_price
        })) || [],
        createdAt: invoice?.created_at,
        updatedAt: invoice?.updated_at
      }));
    } catch (error) {
      console.error('Get invoices error:', error);
      throw error;
    }
  },

  // Get single invoice with items
  async getById(invoiceId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.select(`
          *,
          customer:customers(id, company_name, email, phone, contact_person),
          created_by_user:user_profiles(id, first_name, last_name, email)
        `)?.eq('id', invoiceId)?.single();

      if (invoiceError) throw invoiceError;

      const { data: items, error: itemsError } = await supabase?.from('invoice_items')?.select('*')?.eq('invoice_id', invoiceId)?.order('sort_order', { ascending: true });

      if (itemsError) throw itemsError;

      return {
        ...toCamelCase(invoice),
        customer: invoice?.customer ? toCamelCase(invoice?.customer) : null,
        createdByUser: invoice?.created_by_user ? toCamelCase(invoice?.created_by_user) : null,
        items: items?.map(item => toCamelCase(item))
      };
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  },

  // Create new invoice
  async create(invoiceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's tenant_id
      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

      if (!profile) throw new Error('User profile not found');

      const invoiceToInsert = {
        ...toSnakeCase(invoiceData),
        tenant_id: profile?.tenant_id,
        created_by: user?.id
      };

      const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.insert(invoiceToInsert)?.select()?.single();

      if (invoiceError) throw invoiceError;

      // Insert invoice items if provided
      if (invoiceData?.items && invoiceData?.items?.length > 0) {
        const itemsToInsert = invoiceData?.items?.map((item, index) => ({
          ...toSnakeCase(item),
          invoice_id: invoice?.id,
          sort_order: index
        }));

        const { error: itemsError } = await supabase?.from('invoice_items')?.insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      return toCamelCase(invoice);
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  // Update invoice
  async update(invoiceId, invoiceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const invoiceToUpdate = toSnakeCase(invoiceData);

      const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.update(invoiceToUpdate)?.eq('id', invoiceId)?.select()?.single();

      if (invoiceError) throw invoiceError;

      // Update items if provided
      if (invoiceData?.items) {
        // Delete existing items
        await supabase?.from('invoice_items')?.delete()?.eq('invoice_id', invoiceId);

        // Insert new items
        if (invoiceData?.items?.length > 0) {
          const itemsToInsert = invoiceData?.items?.map((item, index) => ({
            ...toSnakeCase(item),
            invoice_id: invoiceId,
            sort_order: index
          }));

          const { error: itemsError } = await supabase?.from('invoice_items')?.insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }
      }

      return toCamelCase(invoice);
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  },

  // Delete invoice
  async delete(invoiceId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('invoices')?.delete()?.eq('id', invoiceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  },

  // Update invoice status
  async updateStatus(invoiceId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('invoices')?.update({ status })?.eq('id', invoiceId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      throw error;
    }
  },

  // Update payment status
  async updatePaymentStatus(invoiceId, paymentStatus) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('invoices')?.update({ payment_status: paymentStatus })?.eq('id', invoiceId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  },

  // Bulk operations
  async bulkUpdateStatus(invoiceIds, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('invoices')?.update({ status })?.in('id', invoiceIds)?.select();

      if (error) throw error;
      return data?.map(invoice => toCamelCase(invoice));
    } catch (error) {
      console.error('Error bulk updating status:', error);
      throw error;
    }
  },

  // Get next invoice number
  async getNextInvoiceNumber() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

      if (!profile) throw new Error('User profile not found');

      const { data: invoices } = await supabase?.from('invoices')?.select('invoice_number')?.eq('tenant_id', profile?.tenant_id)?.order('created_at', { ascending: false })?.limit(1);

      if (!invoices || invoices?.length === 0) {
        const year = new Date()?.getFullYear();
        return `INV-${year}-001`;
      }

      const lastNumber = invoices?.[0]?.invoice_number;
      const match = lastNumber?.match(/INV-(\d{4})-(\d{3})/);
      
      if (match) {
        const year = new Date()?.getFullYear();
        const lastYear = parseInt(match?.[1]);
        const lastSeq = parseInt(match?.[2]);

        if (year === lastYear) {
          const nextSeq = (lastSeq + 1)?.toString()?.padStart(3, '0');
          return `INV-${year}-${nextSeq}`;
        } else {
          return `INV-${year}-001`;
        }
      }

      return `INV-${new Date()?.getFullYear()}-001`;
    } catch (error) {
      console.error('Error getting next invoice number:', error);
      return `INV-${new Date()?.getFullYear()}-001`;
    }
  }
};